import React from 'react';
import { ArrowRight, CircleCheck, CircleDashed, Sparkles } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { pluralKey } from '../../utils/plural';

/** The engine's thresholds; the API sends its own copy as `rules`. */
export const DEFAULT_RULES = {
  PICTURE_QUIZ: { minPictures: 4 },
  MEMORY_MATCH: { minWords: 8, picturePairsFrom: 8 },
  SENTENCE_BUILDER: { minSentences: 3 },
  WORD_SPRINT: { minWords: 4 },
};

export const WORD_GAMES = ['PICTURE_QUIZ', 'MEMORY_MATCH', 'SENTENCE_BUILDER', 'WORD_SPRINT'];

/**
 * What one word game still needs, as an i18n key and count: null when it is open.
 * Every game is closed for a lesson without words, so those ask for words first.
 */
export function missingFor(type, lesson, rules = DEFAULT_RULES) {
  const game = lesson.games?.find((g) => g.type === type);
  if (game?.available) return null;
  const left = (min, have) => Math.max(1, min - have);
  switch (type) {
    case 'PICTURE_QUIZ':
      return { key: 'needPictures', count: left(rules.PICTURE_QUIZ.minPictures, lesson.withPictures) };
    case 'MEMORY_MATCH':
      return { key: 'needWords', count: left(rules.MEMORY_MATCH.minWords, lesson.words) };
    case 'SENTENCE_BUILDER':
      return { key: 'needSentences', count: left(rules.SENTENCE_BUILDER.minSentences, lesson.sentences) };
    default:
      return { key: 'needWords', count: left(rules.WORD_SPRINT.minWords, lesson.words) };
  }
}

/**
 * One lesson's checklist for the four word games (built from its words, nothing to author):
 * how many words, pictures and example sentences it has, and what each game still needs.
 */
export default function ArcadeReadiness({ lesson, rules, onOpenWords }) {
  const { t, lang } = useI18n();
  const r = rules || DEFAULT_RULES;
  const g = (key) => `gameAuthoring.games.${key}`;

  return (
    <div className="cg-ready" aria-label={t(g('readinessTitle'))} role="group">
      <div className="cg-ready-hd">
        <Sparkles size={16} aria-hidden="true" className="cg-ready-icon" />
        <span className="cg-ready-title">{t(g('readinessTitle'))}</span>
        <span className="cg-ready-stats">
          <span>{t(pluralKey(g('readinessWords'), lesson.words, lang), { count: lesson.words })}</span>
          <span>{t(g('readinessPictures'), { count: lesson.withPictures })}</span>
          <span>{t(g('readinessSentences'), { count: lesson.sentences })}</span>
        </span>
        <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm cg-ready-link" onClick={onOpenWords}>
          {t(g('openWords'))}
          <ArrowRight aria-hidden="true" />
        </button>
      </div>
      <ul className="cg-ready-list">
        {WORD_GAMES.map((type) => {
          const missing = missingFor(type, lesson, r);
          return (
            <li key={type} className={missing ? 'is-todo' : 'is-ok'}>
              {missing
                ? <CircleDashed size={16} aria-hidden="true" />
                : <CircleCheck size={16} aria-hidden="true" />}
              <span className="cg-ready-text">
                <span className="cg-ready-game">{t(`courseAssess.games.gameType.${type}`)}</span>
                <span className="cg-ready-state">
                  {missing
                    ? t(pluralKey(g(missing.key), missing.count, lang), { count: missing.count })
                    : t(g('ready'))}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      {lesson.longSentences > 0 && (
        <p className="cg-ready-note">
          {t(pluralKey(g('longSentences'), lesson.longSentences, lang), { count: lesson.longSentences })}
        </p>
      )}
    </div>
  );
}
